import { products } from '../data/products';
import { bundles } from '../data/bundles';
import { layers, shadows, hanger, thumbnailCrop } from '../data/layers';

// -----------------------------------------------------------------------------
// Scroll scroll me away won't you scroll me away tonight
// -----------------------------------------------------------------------------
export const scrollTop = () => {
  window.scrollTo(0, 0);
};

// -----------------------------------------------------------------------------
// IMAGE LAYER MAGIC
// -----------------------------------------------------------------------------
export const getProductImageUrl = (product, layer) => {
  return makeProductImageObjects(product).filter(obj => obj.layerName === layer)[0].url;
};

// Make all layers for a given product
// (e.x. jackets need two layers front, full)
export const makeProductImageObjects = product => {
  const cleanProductType = getCleanedCategory(product);
  const catalogNumber = getCatalogNumber(product);
  const imgxPath = categoryToDirectory(cleanProductType);
  const imageCategoryName = imgxPath.includes('_tie') ? 'tie' : imgxPath;
  const layerObjects =
    layers[cleanProductType] &&
    layers[cleanProductType].map(layer => {
      const layerName = layer.name ? `${layer.name}` : '';
      const imageName = `${catalogNumber}-${imageCategoryName}${layerName}`;
      let shirtCollar = null;

      if (cleanProductType === 'Shirt') {
        if (product.catalogName.includes('Wing')) {
          shirtCollar = 'wingtip';
        } else if (product.catalogName.includes('Point')) {
          shirtCollar = 'point';
        } else {
          shirtCollar = 'spread';
        }
      }

      return {
        url: `${process.env.REACT_APP_S3_DOMAIN}${window.gt.lookBuilderAssetsBaseUrl}/${imageName}.png`,
        classStr: `builder-image-${imageCategoryName}${layerName}`,
        catalogNumber,
        cleanProductType,
        productType: product.type,
        shirtCollar,
        layerName,
        thumbnailCrop: thumbnailCrop[cleanProductType],
      };
    });
  return layerObjects;
};

// Make all shadow layers for a given product
// (e.x. jackets a drop shadow image)
export const makeShadowImageObjects = productObjects => {
  const cleanProductType = productObjects[0].cleanProductType;
  const productShadows = shadows[cleanProductType];
  let shadowName, classStr;

  if (!productShadows) {
    return [];
  }

  // handle the shirts
  if (cleanProductType === 'Shirt') {
    const collarType = productObjects[0].shirtCollar;
    shadowName = productShadows.filter(x => x.includes(collarType));
    classStr = shadowName[0].split('.')[0];
  } else {
    shadowName = productShadows[0];
    classStr = shadowName.split('.')[0];
  }

  const path = `${process.env.REACT_APP_S3_DOMAIN}${window.gt.lookBuilderAssetsBaseUrl}/${shadowName}`;

  return [
    {
      url: path,
      classStr: classStr,
      catalogNumber: null,
      cleanProductType: null,
      productType: null,
    },
  ];
};

// Make hanger layer
// (e.x. socks and pants need hanger shown)
export const makeHangerObjects = productObjects => {
  const cleanProductType = productObjects[0].cleanProductType;
  const needsHanger = hanger[cleanProductType];

  if (!needsHanger) {
    return [];
  }

  const hangerName = needsHanger[0];
  const classStr = hangerName;
  const path = `${process.env.REACT_APP_S3_DOMAIN}${window.gt.lookBuilderAssetsBaseUrl}/${hangerName}`;

  return [
    {
      url: path,
      classStr: classStr,
      catalogNumber: null,
      cleanProductType: null,
      productType: null,
    },
  ];
};

// -----------------------------------------------------------------------------
// Why are products and bundles the way they are?
// -----------------------------------------------------------------------------

// allows common access to catalogNumber for product or bundle
export const getCatalogNumber = product =>
  product.catalogNumber || product.products.filter(x => x.catalogNumber.startsWith('1'))[0].catalogNumber;

// allows common access to name for product or bundle
export const getName = product => product.catalogName || product.displayName;

// converts product category to slug style directory
export const categoryToDirectory = category => {
  return category
    .toLowerCase()
    .replace(' ', '_')
    .replace('/', '_')
    .replace('(', '_')
    .replace(')', '_');
};

// maps inconsistent product types to customer friendly names
export const getCleanedCategory = product => {
  const jacketTypes = ['suit', 'tuxedo'];

  if (jacketTypes.indexOf(product.type) > -1) {
    return 'Jacket';
  }

  if (product.type === 'Pant') {
    return 'Pants';
  }

  if (product.type === 'Shoe') {
    return 'Shoes';
  }

  if (product.type === 'Tie' && !product.catalogName.includes('Bow')) {
    return 'Long Tie';
  }

  if (product.type === 'Tie' && product.catalogName.includes('Bow')) {
    return 'Bow Tie';
  }

  return product.type;
};

// check if product matches customer friendly list categories
export const isProductInListCategory = (category, product) => {
  if (product.type === 'Pants') {
    return false;
  }

  if (product.type === 'Jacket' && category === 'Jacket & Pants') {
    return true;
  }

  if (product.type === 'Vest' && category === 'Vest & Cummerbund') {
    return true;
  }

  if (product.type === 'Cummerbund' && category === 'Vest & Cummerbund') {
    return true;
  }

  if (product.type === 'Belt' && category === 'Belt & Suspenders') {
    return true;
  }

  if (product.type === 'Suspenders' && category === 'Belt & Suspenders') {
    return true;
  }

  if (product.type === 'Shoe' && category === 'Shoes') {
    return true;
  }

  if (product.type === category) {
    return true;
  }

  return false;
};

// allows common query by catalogNumber for product or bundle
export const getProductByCatalogNumber = catalogNumber => {
  const product = products.filter(product => product.catalogNumber === catalogNumber)[0];

  if (product) {
    return product;
  }

  const jacketFromBundle = bundles
    .filter(bundle => {
      if (bundle.displayName.includes('RECOMMENDED') > 0) {
        return false;
      }
      return true;
    })
    .reduce((acc, bundle) => {
      bundle.products.forEach(product => {
        if (product.catalogNumber === catalogNumber) {
          acc.push(product);
        }
      });
      return acc;
    }, [])[0];

  if (jacketFromBundle) {
    return jacketFromBundle;
  }

  return null;
};

// -----------------------------------------------------------------------------
// Select / Deslect State
// -----------------------------------------------------------------------------
export const selectItem = (selectedCatalogNumbers, category, product, isBundle = false) => {
  return [
    ...selectedCatalogNumbers.filter(x => x.name !== category),
    {
      name: category,
      activeItem: isBundle ? product.products.filter(x => x.type === 'Jacket')[0].catalogNumber : product.catalogNumber,
      bundleName: isBundle ? product.displayName : null,
    },
  ];
};

export const deselectItem = (selectedCatalogNumbers, category) => [
  ...selectedCatalogNumbers.filter(x => x.name !== category),
  {
    name: category,
    activeItem: null,
  },
];

// -----------------------------------------------------------------------------
// Products & Bundle Data
// -----------------------------------------------------------------------------
export const productsByCat = products
  .sort((a, b) => {
    const elm1 = a.type;
    const elm2 = b.type;
    if (elm1 < elm2) {
      return -1;
    }
    if (elm1 > elm2) {
      return 1;
    }
    return 0;
  })
  .reduce((acc, elm, index) => {
    acc[elm.type] = products.filter(x => x.type === elm.type);
    return acc;
  }, {});

export const bundlesByCat = bundles
  .filter(x => {
    if (x.displayName.includes('RECOMMENDED') > 0) {
      return false;
    }
    return true;
  })
  .sort((a, b) => {
    const elm1 = a.type;
    const elm2 = b.type;
    if (elm1 < elm2) {
      return -1;
    }
    if (elm1 > elm2) {
      return 1;
    }
    return 0;
  })
  .reduce((acc, elm, index) => {
    acc[elm.type] = bundles.filter(x => {
      if (x.displayName.includes('RECOMMENDED') > 0) {
        return false;
      }

      if (x.type === elm.type) {
        return true;
      }

      return false;
    });
    return acc;
  }, {});

// -----------------------------------------------------------------------------
// Sorting
// -----------------------------------------------------------------------------
export const sortProductRealPretty = (a, b) => {
  // sort by type first. Vest v Cummerbund, belt v suspenders, etc.
  if (a.type > b.type) {
    return -1;
  }
  if (a.type < b.type) {
    return 1;
  }

  // sort by knot type. Self Tied v Pre Tied
  if (a.type === 'Tie') {
    if (a.attributeSecondary < b.attributeSecondary) {
      return -1;
    }
    if (a.attributeSecondary > b.attributeSecondary) {
      return 1;
    }
  }

  // sort by category. Long Tie v Bow Tie, Collar Type, etc.
  if (a.type === 'Shirt') {
    // we want shirts in reverse order so point collars come last
    if (a.attributePrimary < b.attributePrimary) {
      return -1;
    }
    if (a.attributePrimary > b.attributePrimary) {
      return 1;
    }
  }
  if (a.type === 'Tie' || a.type === 'Vest') {
    if (a.attributePrimary > b.attributePrimary) {
      return -1;
    }
    if (a.attributePrimary < b.attributePrimary) {
      return 1;
    }
  }

  // lapel pins use tertiary instead primary for the category
  if (a.type === 'Lapel Pin') {
    if (a.attributeTertiary > b.attributeTertiary) {
      return -1;
    }
    if (a.attributeTertiary < b.attributeTertiary) {
      return 1;
    }
  }

  // sort by color
  if (a.type === 'Shirt') {
    // reverse shirts so whites come first
    if (a.colorGroup > b.colorGroup) {
      return -1;
    }
    if (a.colorGroup < b.colorGroup) {
      return 1;
    }
  } else {
    if (a.colorGroup < b.colorGroup) {
      return -1;
    }
    if (a.colorGroup > b.colorGroup) {
      return 1;
    }
  }

  // sort by pattern. Stripes v Paisley, etc.
  if (a.type !== 'Shirt') {
    if (a.pattern < b.pattern) {
      return -1;
    }
    if (a.pattern > b.pattern) {
      return 1;
    }
  } else {
    if (a.pattern < b.pattern) {
      return -1;
    }
    if (a.pattern > b.pattern) {
      return 1;
    }
  }

  return 0;
};

// -----------------------------------------------------------------------------
// Filters
// -----------------------------------------------------------------------------
export const makeColorFilters = (activeCollection, isBundle = false) => {
  if (isBundle) {
    return [
      ...new Set(
        bundles.filter(bundle => bundle.displayName.includes('RECOMMENDED') === false).map(bundle => bundle.color)
      ),
    ].sort((a, b) => (a > b ? 1 : -1));
  } else {
    return [
      ...new Set(
        products
          .filter(product => isProductInListCategory(activeCollection, product))
          .map(product => product.colorGroup)
      ),
    ]
      .sort((a, b) => (a > b ? 1 : -1))
      .filter(a => a !== 'Miscellaneous');
  }
};

export const makePatternFilters = (activeCollection, isBundle = false) => {
  if (isBundle) {
    return undefined;
  } else {
    return [
      ...new Set(
        products.filter(product => isProductInListCategory(activeCollection, product)).map(product => product.pattern)
      ),
    ].sort((a, b) => (a > b ? 1 : -1));
  }
};

export const makeCategoryFilters = (activeCollection, isBundle = false) => {
  if (isBundle) {
    return [
      ...new Set(
        bundles.filter(bundle => bundle.displayName.includes('RECOMMENDED') === false).map(bundle => bundle.type)
      ),
    ].sort((a, b) => (a > b ? 1 : -1));
  } else {
    if (activeCollection === 'Shirt') {
      return ['Pleated'];
    }
    if (activeCollection === 'Tie') {
      return [
        ...new Set(
          products
            .filter(product => isProductInListCategory(activeCollection, product))
            .map(product => product.attributePrimary)
        ),
        ...new Set(
          products
            .filter(product => isProductInListCategory(activeCollection, product))
            .map(product => product.attributeSecondary)
        ),
      ].filter(a => a !== null);
    }
    if (activeCollection === 'Lapel Pin') {
      return [
        ...new Set(
          products
            .filter(product => isProductInListCategory(activeCollection, product))
            .map(product => product.attributeTertiary)
        ),
      ].filter(a => a !== null);
    }
    if (activeCollection === 'Vest & Cummerbund') {
      return [
        ...new Set(
          products.filter(product => isProductInListCategory(activeCollection, product)).map(product => product.type)
        ),
        ...new Set(
          products
            .filter(product => isProductInListCategory(activeCollection, product))
            .map(product => product.attributePrimary)
            .filter(attribute => attribute !== 'Fancy')
        ),
      ].filter(a => a !== null);
    }
  }
};

// maps the display name to a customer friendly string
export const mapFilterName = string => {
  let filterName = string;

  // ties
  if (string === 'Windsor Tie') {
    filterName = 'Long Tie';
  }

  // vests
  if (string === 'not_tailored') {
    filterName = 'Satin';
  }
  return filterName.charAt(0).toUpperCase() + filterName.slice(1);
};
